import debounce from 'lodash/debounce';
import { mapGetters, mapActions } from 'vuex';
import { INTERACTION_STATE } from '@/constants/interaction/constants';
import { MAX_NUMBER_MESSAGES } from '@/constants/smsConstants';
import EcsTimeFormatter from '@/mixins/ecs-time-formatter/EcsTimeFormatter';

const surfaceLightenVar = 'var(--v-surface-lighten2)';
const secundaryLightenVar = 'var(--v-secondary-lighten2)';

export default {
  name: 'EcsSideBarInteractionItemMixin',

  mixins: [EcsTimeFormatter],

  data() {
    return {
      iconMap: {
        onCall: {
          color: 'var(--v-success-base)',
          icon: 'mdi-phone-in-talk',
          statusName: 'onCall'
        },
        onHold: {
          color: 'var(--v-primary-base)',
          icon: 'mdi-pause',
          statusName: 'onHold'
        },
        onHoldTelephony: {
          color: 'var(--v-primary-base)',
          icon: 'mdi-phone-paused',
          statusName: 'onHold'
        },
        [INTERACTION_STATE.consulting]: {
          color: 'var(--v-success-base)',
          icon: 'mdi-account-voice',
          statusName: 'interactionStates.consulting'
        },
        completed: {
          color: 'var(--v-success-base)',
          icon: 'mdi-check-circle-outline',
          statusName: 'completed'
        },
        answeredWithinGoalTime: {
          color: 'var(--v-success-base)',
          icon: 'mdi-check-circle',
          statusName: 'answeredWithinGoalTime'
        },
        [INTERACTION_STATE.delegatedToSupervisor]: {
          color: 'var(--v-info-base)',
          icon: 'mdi-information-outline',
          statusName: 'interactionStates.delegatedToSupervisor'
        },
        away: {
          color: 'var(--v-primary-base)',
          icon: 'mdi-check-circle-outline'
        },
        unavailable: {
          color: 'var(--v-primary-base)',
          icon: 'mdi-alert-circle'
        },
        sentMessage: {
          color: this.$vuetify.theme.dark ? surfaceLightenVar : secundaryLightenVar,
          icon: 'mdi-message-reply-text',
          statusName: 'sent'
        },
        receivedMessage: {
          color: this.$vuetify.theme.dark ? surfaceLightenVar : secundaryLightenVar,
          icon: 'mdi-message-reply-text',
          statusName: 'received'
        },
        typing: {
          color: '#393939',
          icon: 'mdi-dots-horizontal'
        },
        newEmail: {
          color: this.$vuetify.theme.dark ? surfaceLightenVar : secundaryLightenVar,
          icon: 'mdi-email',
          statusName: 'newEmail'
        },
        emailRead: {
          color: this.$vuetify.theme.dark ? surfaceLightenVar : secundaryLightenVar,
          icon: 'mdi-email-open',
          statusName: 'emailRead'
        },
        sentSms: {
          color: this.$vuetify.theme.dark ? surfaceLightenVar : secundaryLightenVar,
          icon: 'mdi-chat-processing-outline',
          statusName: 'sent'
        },
        receivedSms: {
          color: this.$vuetify.theme.dark ? surfaceLightenVar : secundaryLightenVar,
          icon: 'mdi-chat-processing',
          statusName: 'received'
        }
      },
      shouldBlink: false
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('NOTIFICATIONS', ['getNumberOfNotificationsForInteraction']),

    ...mapGetters('ELEMENTS', ['getElementIdsByInteractionId']),

    ...mapGetters('SETTINGS', ['applicationSettings']),

    ...mapGetters('CONTACTS', ['getContactV2']),

    timeAgo() {
      return this.interaction.isHistoric ? this.fromNow(this.interaction.timing.endTime, null, true) : null;
    },

    notifications() {
      return this.getNumberOfNotificationsForInteraction(this.interaction.interactionId);
    }
  },

  watch: {
    notifications(value) {
      if (value > 0) {
        this.shouldBlink = true;
        this.setBlinkingTimeout();
      }
    }
  },

  methods: {
    ...mapActions('CONVERSATIONS', ['loadInteractionSmsHistory']),

    ...mapActions('WINDOWS', ['restoreInteractionWindow', 'setFocusedInteractionId']),

    onSidebarInteractionClick() {
      if (this.interaction.isTelephony && this.interaction.isHistoric) {
        let contactId = this.interaction.crm.contactId || this.interaction.destinationContact.id;
        if (contactId === this.loggedInAgent.id && this.interaction.originatorContact) {
          contactId = this.interaction.originatorContact.id;
        }

        return this.$root.$emit('open-contact-card-actions', {
          contact: this.getContactV2(contactId),
          tab: 'history',
          businessProcessId: this.interaction.routing.businessProcessId
        });
      }

      if (this.interaction.isSms) {
        this.loadInteractionSmsHistory({
          interactionId: this.interaction.interactionId,
          elementId: this.getElementIdsByInteractionId(this.interaction.interactionId)[0],
          maxNumber: MAX_NUMBER_MESSAGES,
          fullHistory: true
        });
      }
      this.restoreInteractionWindow(this.interaction.interactionId);
      this.setFocusedInteractionId(this.interaction.interactionId);
    },

    onShowContextMenu(event) {
      // TODO: Temporarily condition added until ECS can provide a delete historic interaction
      if (!this.interaction.isHistoric || (this.interaction.isHistoric && this.interaction.isTelephony)) {
        const clientRect = event.target.getBoundingClientRect();

        this.$root.$emit('open-interaction-context-menu', {
          interaction: this.interaction,
          x: clientRect.x,
          y: clientRect.y
        });
      }
    },

    setBlinkingTimeout: debounce(function() {
      this.shouldBlink = false;
    }, 2000)
  }
};
